import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Minus, FileText, Download, Send, Check, Phone, Mail, MapPin, Star, Clock, Shield, Sparkles, CheckCircle2, User, MessageSquare, ChevronRight, Award, Zap, Ruler, Info, Package, Lock, AlertTriangle, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { demoProfessional, formatPrice, formatMetrosLineares, parseMetrosLineares, calcularOrcamentoMarcenaria, marcenariaTextoTransparencia } from '../data/orcamentoMock';
import jsPDF from 'jspdf';

const API = process.env.REACT_APP_BACKEND_URL;

const OrcamentoPublico = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [professional, setProfessional] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedServices, setSelectedServices] = useState([]);
  const [clientData, setClientData] = useState({ name: '', phone: '', notes: '' });
  const [step, setStep] = useState(1);
  const [generated, setGenerated] = useState(false);
  
  // Estado de acesso/assinatura
  const [accessStatus, setAccessStatus] = useState({
    checked: false,
    hasAccess: true, // Default true para não bloquear enquanto verifica
    isTrial: false,
    trialDaysLeft: 0,
    message: '',
    paymentLink: null
  });

  // Estados para Marcenaria
  const [marcenariaData, setMarcenariaData] = useState({
    ambiente: '',
    metrosLineares: '',
    material: '',
    adicionais: []
  });

  // Cores da empresa (padrão ou personalizadas)
  const getColors = () => {
    if (professional?.cores) {
      return professional.cores;
    }
    return {
      primary: '#0f172a',
      secondary: '#334155',
      accent: '#f1f5f9'
    };
  };

  // Função para escurecer cor (usado para hover)
  const darkenColor = (hex, percent) => {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) - amt;
    const G = (num >> 8 & 0x00FF) - amt;
    const B = (num & 0x0000FF) - amt;
    return `#${(1 << 24 | (R < 255 ? R < 1 ? 0 : R : 255) << 16 | (G < 255 ? G < 1 ? 0 : G : 255) << 8 | (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1)}`;
  };

  // Handler para input de metros lineares (formatação automática)
  const handleMetrosLineares = (value) => {
    const formatted = formatMetrosLineares(value);
    setMarcenariaData(prev => ({ ...prev, metrosLineares: formatted }));
  };

  // Toggle adicional de marcenaria
  const toggleAdicional = (adicionalId) => {
    setMarcenariaData(prev => ({
      ...prev,
      adicionais: prev.adicionais.includes(adicionalId)
        ? prev.adicionais.filter(id => id !== adicionalId)
        : [...prev.adicionais, adicionalId]
    }));
  };

  // Calcular orçamento de marcenaria (memoizado para evitar recálculos desnecessários)
  const orcamentoMarcenaria = React.useMemo(() => {
    if (professional?.isMarcenaria && marcenariaData.material && marcenariaData.metrosLineares) {
      const ml = parseMetrosLineares(marcenariaData.metrosLineares);
      if (ml > 0) {
        return calcularOrcamentoMarcenaria({
          ambiente: marcenariaData.ambiente,
          metrosLineares: ml,
          material: marcenariaData.material,
          adicionaisSelecionados: marcenariaData.adicionais,
          marcenariaConfig: professional.marcenariaConfig
        });
      }
    }
    return null;
  }, [marcenariaData, professional]);

  // Verificar se é marcenaria
  const isMarcenaria = professional?.isMarcenaria === true;

  useEffect(() => {
    const loadProfessional = async () => {
      try {
        // Primeiro tenta carregar do backend
        const res = await fetch(`${API}/api/assinantes/${slug}`);
        if (res.ok) {
          const data = await res.json();
          setProfessional(data);
        } else {
          // Fallback para localStorage ou demo
          const stored = localStorage.getItem(`orcamento_${slug}`);
          if (stored) {
            setProfessional(JSON.parse(stored));
          } else if (slug === 'joao-montador' || slug === 'demo') {
            setProfessional(demoProfessional);
          }
        }
        
        // Verifica status de acesso/assinatura
        const accessRes = await fetch(`${API}/api/subscriptions/check-access/${slug}`);
        if (accessRes.ok) {
          const accessData = await accessRes.json();
          setAccessStatus({
            checked: true,
            hasAccess: accessData.has_access,
            isTrial: accessData.is_trial || false,
            trialDaysLeft: accessData.trial_days_left || 0,
            message: accessData.message,
            paymentLink: accessData.payment_link,
            status: accessData.status,
            reason: accessData.reason
          });
        } else {
          // Se não encontrou assinatura, bloqueia
          setAccessStatus({
            checked: true,
            hasAccess: false,
            isTrial: false,
            trialDaysLeft: 0,
            message: 'Este orçamento não está disponível.',
            paymentLink: null,
            reason: 'not_found'
          });
        }
      } catch (e) {
        const stored = localStorage.getItem(`orcamento_${slug}`);
        if (stored) {
          setProfessional(JSON.parse(stored));
        } else if (slug === 'joao-montador' || slug === 'demo') {
          setProfessional(demoProfessional);
        }
        // Em caso de erro de conexão, assume acesso para não bloquear
        setAccessStatus({
          checked: true,
          hasAccess: true,
          isTrial: false,
          trialDaysLeft: 0,
          message: '',
          paymentLink: null
        });
      }
      setLoading(false);
    };
    
    loadProfessional();
  }, [slug]);

  const handleQuantityChange = (serviceId, delta) => {
    setSelectedServices(prev => {
      const existing = prev.find(s => s.id === serviceId);
      if (existing) {
        const newQty = Math.max(0, existing.quantity + delta);
        if (newQty === 0) {
          return prev.filter(s => s.id !== serviceId);
        }
        return prev.map(s => s.id === serviceId ? { ...s, quantity: newQty } : s);
      } else if (delta > 0) {
        const service = professional.services.find(s => s.id === serviceId);
        return [...prev, { ...service, quantity: 1 }];
      }
      return prev;
    });
  };

  const getQuantity = (serviceId) => {
    const selected = selectedServices.find(s => s.id === serviceId);
    return selected ? selected.quantity : 0;
  };

  const getTotal = () => {
    if (isMarcenaria && orcamentoMarcenaria) {
      return orcamentoMarcenaria.total;
    }
    return selectedServices.reduce((sum, s) => sum + (s.price * s.quantity), 0);
  };

  const colors = professional ? getColors() : { primary: '#1e40af', secondary: '#3b82f6', accent: '#dbeafe' };

  const generatePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Converter hex para RGB
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : { r: 30, g: 64, b: 175 };
    };
    
    const primaryRgb = hexToRgb(colors.primary);
    
    // Header com cor da empresa
    doc.setFillColor(primaryRgb.r, primaryRgb.g, primaryRgb.b);
    doc.rect(0, 0, pageWidth, 45, 'F');
    
    // Logo/Nome da empresa
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(26);
    doc.setFont('helvetica', 'bold');
    doc.text(professional.business || professional.name, 20, 28);
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text('ORÇAMENTO PROFISSIONAL', pageWidth - 20, 28, { align: 'right' });
    
    // Data e validade
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(10);
    const today = new Date().toLocaleDateString('pt-BR');
    doc.text(`Data: ${today}`, 20, 60);
    doc.text(`Válido por 15 dias`, pageWidth - 20, 60, { align: 'right' });
    
    // Box do cliente
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(20, 70, pageWidth - 40, 30, 3, 3, 'F');
    
    doc.setTextColor(primaryRgb.r, primaryRgb.g, primaryRgb.b);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('CLIENTE', 28, 82);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text(clientData.name, 28, 92);
    doc.text(clientData.phone, 100, 92);
    
    // Tabela de serviços
    doc.setTextColor(primaryRgb.r, primaryRgb.g, primaryRgb.b);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('SERVIÇOS SOLICITADOS', 20, 115);
    
    // Header da tabela
    doc.setFillColor(primaryRgb.r, primaryRgb.g, primaryRgb.b);
    doc.rect(20, 120, pageWidth - 40, 12, 'F');
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.text('Descrição', 25, 128);
    doc.text('Qtd', 115, 128);
    doc.text('Unitário', 135, 128);
    doc.text('Subtotal', pageWidth - 25, 128, { align: 'right' });
    
    // Itens
    doc.setTextColor(40, 40, 40);
    doc.setFont('helvetica', 'normal');
    let y = 140;
    
    selectedServices.forEach((service, index) => {
      const subtotal = service.price * service.quantity;
      
      // Alternância de cor de fundo
      if (index % 2 === 0) {
        doc.setFillColor(250, 250, 250);
        doc.rect(20, y - 6, pageWidth - 40, 12, 'F');
      }
      
      doc.text(service.name, 25, y);
      doc.text(service.quantity.toString(), 118, y);
      doc.text(formatPrice(service.price), 135, y);
      doc.setFont('helvetica', 'bold');
      doc.text(formatPrice(subtotal), pageWidth - 25, y, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      y += 12;
    });
    
    // Total
    doc.setFillColor(primaryRgb.r, primaryRgb.g, primaryRgb.b);
    doc.roundedRect(100, y + 5, pageWidth - 120, 18, 3, 3, 'F');
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text('TOTAL:', 108, y + 17);
    doc.text(formatPrice(getTotal()), pageWidth - 28, y + 17, { align: 'right' });
    
    // Observações
    if (clientData.notes) {
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.setFont('helvetica', 'normal');
      doc.text('Observações:', 20, y + 40);
      doc.setTextColor(60, 60, 60);
      
      const splitNotes = doc.splitTextToSize(clientData.notes, pageWidth - 40);
      doc.text(splitNotes, 20, y + 48);
    }
    
    // Footer
    const footerY = 265;
    doc.setFillColor(primaryRgb.r, primaryRgb.g, primaryRgb.b);
    doc.rect(0, footerY, pageWidth, 35, 'F');
    
    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.text(professional.business || professional.name, pageWidth / 2, footerY + 12, { align: 'center' });
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    const contactInfo = [professional.phone, professional.email].filter(Boolean).join(' | ');
    doc.text(contactInfo, pageWidth / 2, footerY + 20, { align: 'center' });
    
    doc.setFontSize(8);
    doc.setTextColor(200, 200, 200);
    doc.text('Orçamento gerado por AtivaJá', pageWidth / 2, footerY + 28, { align: 'center' });
    
    doc.save(`orcamento-${clientData.name.split(' ')[0].toLowerCase()}-${today.replace(/\//g, '-')}.pdf`);
  };

  const handleGenerateAndSend = () => {
    generatePDF();
    setGenerated(true);
    setStep(3);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="text-center">
          <div className="relative w-16 h-16 mx-auto mb-6">
            <div className="absolute inset-0 rounded-full border-4 border-white/10"></div>
            <div className="absolute inset-0 rounded-full border-4 border-t-white animate-spin"></div>
          </div>
          <p className="text-white/60 font-medium">Carregando orçamento...</p>
        </div>
      </div>
    );
  }

  // Tela de bloqueio quando não tem acesso (trial expirado, não pagou, etc.)
  if (accessStatus.checked && !accessStatus.hasAccess) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 bg-red-500/10 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-8 border border-red-500/20">
            <Lock size={40} className="text-red-400" />
          </div>
          
          <h1 className="text-3xl font-bold text-white mb-4">
            {accessStatus.reason === 'trial_expired' ? 'Período de teste expirado' : 
             accessStatus.reason === 'suspended' ? 'Assinatura suspensa' :
             accessStatus.reason === 'cancelled' ? 'Assinatura cancelada' :
             'Acesso indisponível'}
          </h1>
          
          <p className="text-white/60 mb-8 text-lg">{accessStatus.message}</p>
          
          {accessStatus.reason === 'trial_expired' && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-8">
              <div className="flex items-center gap-3 text-amber-400">
                <AlertTriangle size={20} />
                <p className="text-sm text-left">
                  O período de teste de 7 dias terminou. Assine agora para continuar usando o sistema e não perder seus clientes!
                </p>
              </div>
            </div>
          )}
          
          {accessStatus.paymentLink ? (
            <a
              href={accessStatus.paymentLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-green-500 hover:bg-green-400 text-white rounded-xl font-semibold text-lg transition-all shadow-lg hover:shadow-xl hover:shadow-green-500/30 hover:scale-105 mb-4 w-full"
            >
              <CreditCard size={20} />
              Assinar agora - R$ 29,99/mês
            </a>
          ) : (
            <Button
              onClick={() => navigate('/orcamento')}
              className="w-full h-14 bg-white/10 border-white/20 text-white hover:bg-white/20 rounded-xl"
            >
              Configurar orçamento
            </Button>
          )}
          
          <p className="text-white/40 text-sm mt-6">
            Dúvidas? Entre em contato pelo WhatsApp
          </p>
        </div>
      </div>
    );
  }

  if (!professional) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 bg-white/5 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-8 border border-white/10">
            <FileText size={40} className="text-white/40" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">Orçamento não encontrado</h1>
          <p className="text-white/60 mb-10 text-lg">Este link não existe ou foi desativado pelo profissional.</p>
          <Button onClick={() => navigate('/orcamento')} variant="outline" className="bg-white/10 border-white/20 text-white hover:bg-white/20">
            <ArrowLeft size={18} className="mr-2" />
            Voltar ao início
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header Personalizado Premium */}
      <header className="relative overflow-hidden">
        {/* Background com gradiente da cor da empresa */}
        <div 
          className="absolute inset-0"
          style={{ 
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary || darkenColor(colors.primary, 20)} 50%, ${colors.primary} 100%)` 
          }}
        />
        
        {/* Logo como Banner de fundo com transparência */}
        {professional.logo && (
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {/* Logo grande centralizada/direita como marca d'água */}
            <img 
              src={professional.logo}
              alt=""
              className="absolute right-[5%] top-1/2 -translate-y-1/2 h-[250px] md:h-[350px] w-auto opacity-[0.12]"
              style={{
                filter: 'brightness(300%)',
                mixBlendMode: 'soft-light'
              }}
            />
          </div>
        )}
        
        {/* Padrão decorativo abstrato */}
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-10"
            style={{ background: `radial-gradient(circle, white 0%, transparent 70%)` }}
          />
          <div 
            className="absolute -bottom-32 -left-32 w-80 h-80 rounded-full opacity-10"
            style={{ background: `radial-gradient(circle, white 0%, transparent 70%)` }}
          />
          <div 
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] opacity-5"
            style={{ 
              background: `conic-gradient(from 0deg, transparent, white, transparent, white, transparent)`,
              borderRadius: '50%'
            }}
          />
        </div>
        
        <div className="relative max-w-4xl mx-auto px-4 py-10 md:py-14">
          {/* Logo e Nome */}
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 mb-8">
            {professional.logo ? (
              <img 
                src={professional.logo} 
                alt={professional.business}
                className="w-20 h-20 md:w-24 md:h-24 rounded-2xl object-cover bg-white shadow-2xl ring-4 ring-white/20"
              />
            ) : (
              <div 
                className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-white/15 backdrop-blur-md flex items-center justify-center text-white text-3xl md:text-4xl font-bold shadow-2xl ring-4 ring-white/20"
              >
                {(professional.business || professional.name).charAt(0).toUpperCase()}
              </div>
            )}
            
            <div className="text-center sm:text-left flex-1">
              <h1 className="text-2xl md:text-4xl font-bold text-white tracking-tight">
                {professional.business || professional.name}
              </h1>
              <div className="flex items-center justify-center sm:justify-start gap-2 mt-2 text-white/80">
                <Sparkles size={16} className="text-yellow-300" />
                <span className="text-sm md:text-base font-medium">Orçamento Online Instantâneo</span>
              </div>
              
              {/* Contato */}
              {(professional.phone || professional.email) && (
                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-4 mt-4 text-white/70 text-sm">
                  {professional.phone && (
                    <span className="flex items-center gap-1.5">
                      <Phone size={14} />
                      {professional.phone}
                    </span>
                  )}
                  {professional.email && (
                    <span className="flex items-center gap-1.5">
                      <Mail size={14} />
                      {professional.email}
                    </span>
                  )}
                </div>
              )}
            </div>
            
            {/* Botão WhatsApp Desktop */}
            {professional.phone && (
              <a
                href={`https://wa.me/55${professional.phone.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden md:flex items-center gap-2 px-5 py-2.5 bg-green-500 hover:bg-green-400 text-white rounded-full transition-all shadow-lg hover:shadow-green-500/30 font-medium"
              >
                <Phone size={18} />
                <span>WhatsApp</span>
              </a>
            )}
          </div>
          
          {/* Trust badges */}
          <div className="flex flex-wrap justify-center sm:justify-start gap-3 md:gap-6">
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-white/90 text-sm">
              <Shield size={16} className="text-green-300" />
              <span>Sem compromisso</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-white/90 text-sm">
              <Zap size={16} className="text-yellow-300" />
              <span>Resposta rápida</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-white/90 text-sm">
              <Award size={16} className="text-blue-300" />
              <span>Profissional verificado</span>
            </div>
          </div>
        </div>
      </header>

      {/* Banner de Trial (quando em período de teste) */}
      {accessStatus.isTrial && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
          <div className="max-w-4xl mx-auto px-4 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm">
              <Clock size={16} />
              <span>
                <strong>Período de teste:</strong> {accessStatus.trialDaysLeft} {accessStatus.trialDaysLeft === 1 ? 'dia restante' : 'dias restantes'}
              </span>
            </div>
            <a
              href={accessStatus.paymentLink || `/orcamento/assinar/${slug}`}
              target={accessStatus.paymentLink ? "_blank" : "_self"}
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-1.5 bg-white text-amber-600 rounded-full text-sm font-semibold hover:bg-amber-50 transition-colors shadow-md hover:shadow-lg"
            >
              <CreditCard size={14} />
              Assine agora - R$ 29,99/mês
            </a>
          </div>
        </div>
      )}

      {/* Progress Steps - Sticky */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {[
              { label: 'Serviços', icon: FileText },
              { label: 'Seus dados', icon: User },
              { label: 'Finalizar', icon: CheckCircle2 }
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="flex items-center flex-1">
                  <div className="flex items-center gap-2">
                    <div 
                      className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 ${
                        step > i + 1 
                          ? 'bg-green-500 text-white shadow-lg shadow-green-500/30' 
                          : step === i + 1 
                            ? 'text-white shadow-lg'
                            : 'bg-slate-100 text-slate-400'
                      }`}
                      style={step === i + 1 ? { backgroundColor: colors.primary, boxShadow: `0 10px 25px -5px ${colors.primary}40` } : {}}
                    >
                      {step > i + 1 ? <Check size={20} /> : <Icon size={20} />}
                    </div>
                    <span className={`text-sm hidden sm:inline font-medium ${
                      step === i + 1 ? 'text-slate-900' : step > i + 1 ? 'text-green-600' : 'text-slate-400'
                    }`}>
                      {item.label}
                    </span>
                  </div>
                  {i < 2 && (
                    <div className={`flex-1 h-1 mx-3 rounded-full transition-all duration-500 ${
                      step > i + 1 ? 'bg-green-500' : 'bg-slate-200'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-8 pb-32 sm:pb-8">
        {step === 1 && !isMarcenaria && (
          <div className="space-y-6">
            {/* Título da seção */}
            <div className="text-center sm:text-left">
              <h2 className="text-2xl font-bold text-slate-900">Escolha seus serviços</h2>
              <p className="text-slate-500 mt-1">Selecione os serviços e quantidades desejadas</p>
            </div>

            {/* Lista de serviços - Cards modernos */}
            <div className="grid gap-4">
              {professional.services?.map((service, index) => {
                const qty = getQuantity(service.id);
                const isSelected = qty > 0;
                
                return (
                  <div
                    key={service.id}
                    data-testid={`service-card-${service.id}`}
                    className={`group relative bg-white rounded-2xl border-2 transition-all duration-300 overflow-hidden ${
                      isSelected 
                        ? 'border-transparent shadow-xl scale-[1.02]' 
                        : 'border-slate-200 hover:border-slate-300 hover:shadow-lg'
                    }`}
                    style={isSelected ? { 
                      borderColor: colors.primary,
                      boxShadow: `0 20px 40px -15px ${colors.primary}30`
                    } : {}}
                  >
                    {/* Indicador lateral de seleção */}
                    <div 
                      className={`absolute left-0 top-0 bottom-0 w-1.5 transition-all duration-300 ${isSelected ? 'opacity-100' : 'opacity-0'}`}
                      style={{ backgroundColor: colors.primary }}
                    />
                    
                    <div className="p-5 pl-6 flex items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-slate-900 text-lg truncate">{service.name}</h3>
                          {isSelected && (
                            <span 
                              className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-white"
                              style={{ backgroundColor: colors.primary }}
                            >
                              <Check size={12} />
                              Selecionado
                            </span>
                          )}
                        </div>
                        <div className="flex items-baseline gap-2 mt-1">
                          <span 
                            className="text-2xl font-bold"
                            style={{ color: colors.primary }}
                          >
                            {formatPrice(service.price)}
                          </span>
                          <span className="text-slate-400 text-sm">/ {service.unit}</span>
                        </div>
                      </div>
                      
                      {/* Controles de quantidade */}
                      <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1">
                        <button
                          onClick={() => handleQuantityChange(service.id, -1)}
                          disabled={qty === 0}
                          data-testid={`decrease-${service.id}`}
                          className="w-10 h-10 rounded-lg bg-white flex items-center justify-center text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm disabled:shadow-none"
                        >
                          <Minus size={18} />
                        </button>
                        <span 
                          className={`w-12 text-center text-xl font-bold transition-colors ${
                            isSelected ? '' : 'text-slate-300'
                          }`}
                          style={isSelected ? { color: colors.primary } : {}}
                          data-testid={`quantity-${service.id}`}
                        >
                          {qty}
                        </span>
                        <button
                          onClick={() => handleQuantityChange(service.id, 1)}
                          data-testid={`increase-${service.id}`}
                          className="w-10 h-10 rounded-lg flex items-center justify-center text-white transition-all shadow-md hover:shadow-lg hover:scale-105"
                          style={{ backgroundColor: colors.primary }}
                        >
                          <Plus size={18} />
                        </button>
                      </div>
                    </div>
                    
                    {/* Subtotal quando selecionado */}
                    {isSelected && (
                      <div 
                        className="px-6 py-3 border-t flex items-center justify-between"
                        style={{ backgroundColor: `${colors.primary}08`, borderColor: `${colors.primary}20` }}
                      >
                        <span className="text-sm text-slate-600">Subtotal ({qty}x)</span>
                        <span className="font-bold" style={{ color: colors.primary }}>
                          {formatPrice(service.price * qty)}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Resumo flutuante */}
            {selectedServices.length > 0 && (
              <div 
                className="fixed bottom-0 left-0 right-0 sm:relative sm:mt-6 bg-white sm:rounded-2xl shadow-2xl sm:shadow-lg border-t sm:border border-slate-200 p-4 sm:p-6 z-40"
                data-testid="cart-summary"
              >
                <div className="max-w-4xl mx-auto">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-bold"
                          style={{ backgroundColor: colors.primary }}
                        >
                          {selectedServices.reduce((sum, s) => sum + s.quantity, 0)}
                        </div>
                        <span className="text-slate-600 text-sm sm:text-base">
                          {selectedServices.length} serviço(s) selecionado(s)
                        </span>
                      </div>
                      <div className="flex items-baseline gap-2 mt-1">
                        <span className="text-slate-500 text-sm">Total:</span>
                        <span 
                          className="text-2xl sm:text-3xl font-bold"
                          style={{ color: colors.primary }}
                          data-testid="total-price"
                        >
                          {formatPrice(getTotal())}
                        </span>
                      </div>
                    </div>
                    <Button 
                      onClick={() => setStep(2)} 
                      data-testid="continue-button"
                      className="h-12 sm:h-14 px-6 sm:px-8 text-white rounded-xl font-semibold text-base sm:text-lg shadow-lg hover:shadow-xl transition-all hover:scale-105"
                      style={{ backgroundColor: colors.primary }}
                    >
                      Continuar
                      <ChevronRight size={20} className="ml-1" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 1 para MARCENARIA - Calculadora por Metro Linear */}
        {step === 1 && isMarcenaria && (
          <div className="space-y-6">
            {/* Título da seção */}
            <div className="text-center sm:text-left">
              <div className="flex items-center gap-3 justify-center sm:justify-start mb-2">
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-white"
                  style={{ backgroundColor: colors.primary }}
                >
                  <Ruler size={20} />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">Calcule seu orçamento</h2>
              </div>
              <p className="text-slate-500 mt-1">Informe os dados do ambiente para calcular automaticamente</p>
            </div>

            {/* Card Principal - Calculadora */}
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
              {/* Ambiente */}
              <div className="p-6 border-b border-slate-100">
                <Label className="text-slate-700 font-semibold flex items-center gap-2 mb-3">
                  <Package size={16} style={{ color: colors.primary }} />
                  Ambiente
                </Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    ...(professional.marcenariaConfig?.ambientes?.filter(a => a.ativo) || []),
                    ...(professional.marcenariaConfig?.ambientesCustom?.filter(a => a.ativo) || [])
                  ].map((ambiente) => (
                    <button
                      key={ambiente.id}
                      onClick={() => setMarcenariaData(prev => ({ ...prev, ambiente: ambiente.id }))}
                      data-testid={`ambiente-${ambiente.id}`}
                      className={`px-4 py-3 rounded-xl border-2 font-medium transition-all ${
                        marcenariaData.ambiente === ambiente.id
                          ? 'text-white shadow-lg'
                          : 'border-slate-200 text-slate-700 hover:border-slate-300 bg-white'
                      }`}
                      style={marcenariaData.ambiente === ambiente.id ? { 
                        backgroundColor: colors.primary, 
                        borderColor: colors.primary 
                      } : {}}
                    >
                      {ambiente.nome}
                    </button>
                  ))}
                </div>
              </div>

              {/* Metros Lineares */}
              <div className="p-6 border-b border-slate-100">
                <Label className="text-slate-700 font-semibold flex items-center gap-2 mb-3">
                  <Ruler size={16} style={{ color: colors.primary }} />
                  Medida (metros lineares)
                </Label>
                <div className="relative">
                  <Input
                    type="text"
                    inputMode="numeric"
                    placeholder="Ex: 4,50"
                    value={marcenariaData.metrosLineares}
                    onChange={(e) => handleMetrosLineares(e.target.value)}
                    data-testid="metros-lineares-input"
                    className="h-14 text-2xl font-bold text-center pr-16"
                    style={{ color: colors.primary }}
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 font-medium">
                    metros
                  </span>
                </div>
                <p className="text-sm text-slate-500 mt-2 text-center">
                  Digite apenas números. Ex: 450 = 4,50m
                </p>
              </div>

              {/* Material */}
              <div className="p-6 border-b border-slate-100">
                <Label className="text-slate-700 font-semibold flex items-center gap-2 mb-3">
                  <Package size={16} style={{ color: colors.primary }} />
                  Material
                </Label>
                <div className="space-y-2">
                  {professional.marcenariaConfig?.materiais?.filter(m => m.ativo).map((material) => (
                    <button
                      key={material.id}
                      onClick={() => setMarcenariaData(prev => ({ ...prev, material: material.id }))}
                      data-testid={`material-${material.id}`}
                      className={`w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all ${
                        marcenariaData.material === material.id
                          ? 'shadow-lg'
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                      style={marcenariaData.material === material.id ? { 
                        backgroundColor: `${colors.primary}10`, 
                        borderColor: colors.primary 
                      } : {}}
                    >
                      <div className="text-left">
                        <p className="font-semibold text-slate-900">{material.nome}</p>
                        <p className="text-sm text-slate-500">{material.descricao}</p>
                      </div>
                      <span 
                        className="text-lg font-bold"
                        style={{ color: colors.primary }}
                      >
                        {formatPrice(material.preco)}/ml
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Adicionais */}
              <div className="p-6">
                <Label className="text-slate-700 font-semibold flex items-center gap-2 mb-3">
                  <Sparkles size={16} style={{ color: colors.primary }} />
                  Adicionais (opcional)
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    ...(professional.marcenariaConfig?.adicionais?.filter(a => a.ativo) || []),
                    ...(professional.marcenariaConfig?.adicionaisCustom?.filter(a => a.ativo) || [])
                  ].map((adicional) => (
                    <button
                      key={adicional.id}
                      onClick={() => toggleAdicional(adicional.id)}
                      data-testid={`adicional-${adicional.id}`}
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all text-left ${
                        marcenariaData.adicionais.includes(adicional.id)
                          ? 'shadow-md'
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                      style={marcenariaData.adicionais.includes(adicional.id) ? { 
                        backgroundColor: `${colors.primary}10`, 
                        borderColor: colors.primary 
                      } : {}}
                    >
                      <div 
                        className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                          marcenariaData.adicionais.includes(adicional.id)
                            ? 'text-white'
                            : 'border-slate-300'
                        }`}
                        style={marcenariaData.adicionais.includes(adicional.id) ? { 
                          backgroundColor: colors.primary, 
                          borderColor: colors.primary 
                        } : {}}
                      >
                        {marcenariaData.adicionais.includes(adicional.id) && <Check size={14} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900">{adicional.nome}</p>
                        <p className="text-sm text-slate-500">
                          {adicional.tipo === 'percentual' 
                            ? `+${adicional.valor}%` 
                            : `+${formatPrice(adicional.valor)}/ml`
                          }
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Resumo do Orçamento */}
            {orcamentoMarcenaria && orcamentoMarcenaria.total > 0 && (
              <div 
                className="fixed bottom-0 left-0 right-0 sm:relative sm:mt-6 bg-white sm:rounded-2xl shadow-2xl sm:shadow-lg border-t sm:border border-slate-200 z-40"
                data-testid="orcamento-marcenaria-summary"
              >
                {/* Detalhes do cálculo */}
                <div className="p-4 sm:p-6 border-b border-slate-100">
                  <p className="text-sm font-medium text-slate-500 mb-3">Detalhes do cálculo:</p>
                  <div className="space-y-2">
                    {orcamentoMarcenaria.detalhes.map((detalhe, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className="text-slate-600">{detalhe.descricao}</span>
                        <span className="font-medium text-slate-900">{formatPrice(detalhe.valor)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Total e botão */}
                <div className="p-4 sm:p-6">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <span className="text-slate-500 text-sm">Total estimado:</span>
                      <p 
                        className="text-3xl sm:text-4xl font-bold"
                        style={{ color: colors.primary }}
                        data-testid="total-marcenaria"
                      >
                        {formatPrice(orcamentoMarcenaria.total)}
                      </p>
                    </div>
                    <Button 
                      onClick={() => setStep(2)} 
                      data-testid="continue-marcenaria-button"
                      className="h-12 sm:h-14 px-6 sm:px-8 text-white rounded-xl font-semibold text-base sm:text-lg shadow-lg hover:shadow-xl transition-all hover:scale-105"
                      style={{ backgroundColor: colors.primary }}
                    >
                      Continuar
                      <ChevronRight size={20} className="ml-1" />
                    </Button>
                  </div>
                </div>
                
                {/* Texto de transparência */}
                <div className="px-4 sm:px-6 pb-4 sm:pb-6">
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2">
                    <Info size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-800">
                      {professional.marcenariaConfig?.textoTransparencia || marcenariaTextoTransparencia}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            {/* Botão voltar */}
            <button
              onClick={() => setStep(1)}
              data-testid="back-to-services"
              className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors group"
            >
              <div className="w-8 h-8 rounded-lg bg-slate-100 group-hover:bg-slate-200 flex items-center justify-center transition-colors">
                <ArrowLeft size={16} />
              </div>
              <span className="font-medium">Voltar aos serviços</span>
            </button>
            
            {/* Card do formulário */}
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
              {/* Header do card */}
              <div 
                className="p-6 border-b"
                style={{ 
                  background: `linear-gradient(135deg, ${colors.primary}08 0%, ${colors.primary}03 100%)`,
                  borderColor: `${colors.primary}15`
                }}
              >
                <div className="flex items-center gap-3">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-white"
                    style={{ backgroundColor: colors.primary }}
                  >
                    <User size={24} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">Seus dados para o orçamento</h2>
                    <p className="text-slate-500 text-sm mt-0.5">Preencha para receber o PDF personalizado</p>
                  </div>
                </div>
              </div>
              
              {/* Formulário */}
              <div className="p-6 space-y-5">
                <div>
                  <Label htmlFor="clientName" className="text-slate-700 font-semibold flex items-center gap-2">
                    <User size={16} className="text-slate-400" />
                    Seu nome completo
                  </Label>
                  <Input
                    id="clientName"
                    data-testid="client-name-input"
                    value={clientData.name}
                    onChange={(e) => setClientData({ ...clientData, name: e.target.value })}
                    placeholder="Como devemos chamar você?"
                    className="mt-2 h-14 rounded-xl border-slate-200 focus:border-slate-400 text-lg"
                  />
                </div>
                
                <div>
                  <Label htmlFor="clientPhone" className="text-slate-700 font-semibold flex items-center gap-2">
                    <Phone size={16} className="text-slate-400" />
                    WhatsApp para contato
                  </Label>
                  <Input
                    id="clientPhone"
                    data-testid="client-phone-input"
                    value={clientData.phone}
                    onChange={(e) => setClientData({ ...clientData, phone: e.target.value })}
                    placeholder="(00) 00000-0000"
                    className="mt-2 h-14 rounded-xl border-slate-200 focus:border-slate-400 text-lg"
                  />
                </div>
                
                <div>
                  <Label htmlFor="notes" className="text-slate-700 font-semibold flex items-center gap-2">
                    <MessageSquare size={16} className="text-slate-400" />
                    Observações (opcional)
                  </Label>
                  <textarea
                    id="notes"
                    data-testid="client-notes-input"
                    value={clientData.notes}
                    onChange={(e) => setClientData({ ...clientData, notes: e.target.value })}
                    placeholder="Detalhes adicionais, endereço, preferências..."
                    rows={3}
                    className="mt-2 w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-200 focus:border-slate-400 resize-none text-base"
                  />
                </div>
              </div>

              {/* Resumo dos serviços ou Marcenaria */}
              <div className="mx-6 mb-6">
                <div 
                  className="p-5 rounded-xl border"
                  style={{ 
                    backgroundColor: `${colors.primary}05`,
                    borderColor: `${colors.primary}15`
                  }}
                >
                  <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <FileText size={16} style={{ color: colors.primary }} />
                    Resumo do seu orçamento
                  </p>
                  
                  {/* Resumo para Marcenaria */}
                  {isMarcenaria && orcamentoMarcenaria ? (
                    <div className="space-y-2">
                      {orcamentoMarcenaria.detalhes.map((detalhe, idx) => (
                        <div key={idx} className="flex justify-between items-center text-sm">
                          <span className="text-slate-600">{detalhe.descricao}</span>
                          <span className="font-semibold text-slate-800">{formatPrice(detalhe.valor)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* Resumo para serviços normais */
                    <div className="space-y-2">
                      {selectedServices.map(s => (
                        <div key={s.id} className="flex justify-between items-center text-sm">
                          <span className="text-slate-600 flex items-center gap-2">
                            <span 
                              className="w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold text-white"
                              style={{ backgroundColor: colors.primary }}
                            >
                              {s.quantity}
                            </span>
                            {s.name}
                          </span>
                          <span className="font-semibold text-slate-800">{formatPrice(s.price * s.quantity)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div 
                    className="border-t mt-4 pt-4 flex justify-between items-center"
                    style={{ borderColor: `${colors.primary}20` }}
                  >
                    <span className="font-bold text-slate-900 text-lg">Total</span>
                    <span 
                      className="font-bold text-2xl"
                      style={{ color: colors.primary }}
                    >
                      {formatPrice(getTotal())}
                    </span>
                  </div>
                  
                  {/* Texto de transparência para marcenaria */}
                  {isMarcenaria && (
                    <div className="mt-4 pt-4 border-t" style={{ borderColor: `${colors.primary}20` }}>
                      <p className="text-xs text-slate-500 italic">
                        {professional.marcenariaConfig?.textoTransparencia || marcenariaTextoTransparencia}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Botão gerar */}
              <div className="p-6 pt-0">
                <Button
                  onClick={handleGenerateAndSend}
                  disabled={!clientData.name || !clientData.phone}
                  data-testid="generate-pdf-button"
                  className="w-full h-14 rounded-xl text-white font-semibold text-lg disabled:opacity-50 transition-all hover:scale-[1.02] hover:shadow-lg"
                  style={{ 
                    backgroundColor: colors.primary,
                    boxShadow: `0 10px 30px -10px ${colors.primary}50`
                  }}
                >
                  <Download size={20} className="mr-2" />
                  Gerar meu orçamento em PDF
                </Button>
                <p className="text-center text-slate-400 text-sm mt-3">
                  O PDF será baixado automaticamente
                </p>
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="max-w-lg mx-auto text-center py-10">
            {/* Animação de sucesso */}
            <div className="relative mb-8">
              <div 
                className="absolute inset-0 animate-ping opacity-20 rounded-full mx-auto w-28 h-28"
                style={{ backgroundColor: colors.primary }}
              />
              <div 
                className="relative w-28 h-28 rounded-full flex items-center justify-center mx-auto shadow-2xl"
                style={{ 
                  background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary || darkenColor(colors.primary, 10)} 100%)`,
                  boxShadow: `0 20px 50px -15px ${colors.primary}60`
                }}
              >
                <CheckCircle2 size={56} className="text-white" />
              </div>
            </div>
            
            <h2 className="text-3xl font-bold text-slate-900 mb-3">Orçamento gerado!</h2>
            <p className="text-slate-600 text-lg mb-2">
              O PDF foi baixado automaticamente para o seu dispositivo.
            </p>
            <p className="text-slate-500 mb-10">
              Agora é só enviar para <strong style={{ color: colors.primary }}>{professional.business || professional.name}</strong> pelo WhatsApp!
            </p>
            
            {/* Ações */}
            <div className="flex flex-col gap-4">
              <a
                href={`https://wa.me/55${professional.phone.replace(/\D/g, '')}?text=Olá! Acabei de gerar um orçamento pelo site. Meu nome é ${encodeURIComponent(clientData.name)}.`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="send-whatsapp-button"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-green-500 hover:bg-green-400 text-white rounded-2xl font-semibold text-lg transition-all shadow-lg hover:shadow-xl hover:shadow-green-500/30 hover:scale-105"
              >
                <Phone size={22} />
                Enviar pelo WhatsApp
              </a>
              
              <Button
                onClick={() => {
                  setStep(1);
                  setSelectedServices([]);
                  setClientData({ name: '', phone: '', notes: '' });
                  setGenerated(false);
                }}
                variant="outline"
                data-testid="new-quote-button"
                className="h-14 rounded-2xl border-2 border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 font-medium text-base"
              >
                <ArrowLeft size={18} className="mr-2" />
                Fazer novo orçamento
              </Button>
            </div>
            
            {/* Card de informação do profissional */}
            <div 
              className="mt-12 p-6 rounded-2xl border text-left"
              style={{ 
                backgroundColor: `${colors.primary}05`,
                borderColor: `${colors.primary}15`
              }}
            >
              <div className="flex items-center gap-4">
                {professional.logo ? (
                  <img 
                    src={professional.logo} 
                    alt={professional.business}
                    className="w-14 h-14 rounded-xl object-cover"
                  />
                ) : (
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center text-white text-xl font-bold"
                    style={{ backgroundColor: colors.primary }}
                  >
                    {(professional.business || professional.name).charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="font-bold text-slate-900">{professional.business || professional.name}</h3>
                  <p className="text-slate-500 text-sm">{professional.phone}</p>
                </div>
                <Award size={24} style={{ color: colors.primary }} />
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer mobile com WhatsApp */}
      {step === 1 && selectedServices.length === 0 && professional.phone && (
        <div className="sm:hidden fixed bottom-0 left-0 right-0 p-4 bg-white/80 backdrop-blur-lg border-t border-slate-200">
          <a
            href={`https://wa.me/55${professional.phone.replace(/\D/g, '')}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-3.5 bg-green-500 hover:bg-green-400 text-white rounded-xl font-semibold transition-all"
          >
            <Phone size={20} />
            Falar no WhatsApp
          </a>
        </div>
      )}
      
      {/* Powered by - Footer discreto */}
      <div className="text-center py-6 text-slate-400 text-sm">
        Orçamento gerado por <span className="font-medium">AtivaJá</span>
      </div>
    </div>
  );
};

export default OrcamentoPublico;
